import axios from 'axios'
import Queue from '~/lib/queue.js'
import { isAuthStatusError, isBetterAuthApiKey } from '~/lib/johkuApiAuth'
import { createShopAuthClient } from '~/lib/auth-client'
import { clearStaffReasonFlow, confirmBetterAuthSession, suppressSessionRecovery } from '~/lib/sessionRecovery'

let EventBus = null
// Latest payment list request per basket (basketShopIdWithIndex). A pospayments GET can be slow, and if it started before the
// receipt was cleared its response would bring back payments of a finished sale (JDEV-3310), so only the latest request may commit
const paymentListRequests = {}

// nuxt3-vuex-module passes this file's exports to createStore. Without it Vuex enables devtools whenever
// NODE_ENV is not "production", and on the server every request's app then stays in __VUE_DEVTOOLS_PLUGINS__.
export const devtools = false && true

function effectiveApiKey (state) {
  return state.authToken || state.apiKey
}

export const state = () => ({
  events: {},
  settings: {},
  linkedStores: [],
  vouchers: [],
  customers: {},
  billingcustomers: {},
  shopId: '',
  hostName: '',
  user: null,
  apiKey: null,
  authToken: null,
  clientid: null,
  clientName: null,
  clientIdNumer: null,
  manifestprinter: null,
  defaultDeliverymethod: null,
  posDeliveryActive: false,
  tabid: null,
  floating: false,
  floatingReceipt: null,
  selfService: false,
  columnTabsMode: 'row', // the user interface's "Column tabs": row, grid or gridfixed (JDEV-3370, ColumnHeader.vue)
  contract: null,
  layouts: null,
  pos: null,
  view: null,
  baseDomain: '',
  localDomain: '',
  basketIds: {},
  basketItems: null,
  activeBasket: 1,
  activeDeliveries: {},
  payments: {},
  mobilepayPhoneNumber: null,
  mobilepayToken: null,
  activeOrder: null,
  imageIndex: 0,
  timeDiff: false,
  userData: null,
  joinChannel: null,
  editMode: false,
  openCategories: [],
  openResourcepools: [],
  unsavedChanges: false,
  uploads: [], // background image uploads, see lib/uploadqueue.js
  locales: ['fi_FI', 'en_US', 'de_DE', 'ru_RU', 'sv_SE', 'it_IT'], // available langages
  locale: null, // current locale
  systemLocale: null, // system locale
  version: null, // current version
  countries: {},
  incomingOffersShown: false,
  campaignCheckDone: false, // the first campaign feed check has finished and its modal (if any) is closed; incoming offers waits for it
  lastSeenMessageTs: 0,
  showAllFields: false,
  advancedCollections: 0,
  sideMenuOpen: false,
  heartbeatTs: Math.floor(Date.now() / 1000),
  timezone: 'Europe/Helsinki',
  sessionExpired: false,
  sessionNeedsRecovery: false,
  _: {} // store for translations
})

export const mutations = {
  setSettings (state, data) {
    state.settings[data.shopId + '-' + data.locale] = data.settings
    // If there is no locale but a shopid is set, set default locale
    if(state.shopid !== '' && !state.locale && data.shopId === state.shopId) {
      state.locale = data.locale
    }
  },
  setTimezone (state, timezone) {
    state.timezone = timezone
  },
  setEvent (state, data) {
    state.events[data.identifier] = data.handler
  },
  setCustomer (state, data) {
    state.customers[data.index] = data.customer
  },
  setBillingcustomer (state, data) {
    state.billingcustomers[data.index] = data.billingcustomer
  },
  setLinkedStores (state, data) {
    state.linkedStores = data.linkedStores
  },
  setVouchers (state, data) {
    state.vouchers = data.vouchers
  },
  setShopId (state, shopId) {
    state.shopId = shopId
  },
  setHostName (state, hostName) {
    state.hostName = hostName
  },
  setUser (state, user) {
    state.user = user
  },
  setApiKey (state, apiKey) {
    state.apiKey = apiKey
  },
  setAuthToken (state, authToken) {
    state.authToken = authToken
  },
  setClientId (state, clientid) {
    state.clientid = clientid
  },
  setClientName (state, clientName) {
    state.clientName = clientName
  },
  setClientIdNumber(state, clientIdNumber) {
    state.clientIdNumber = clientIdNumber
  },
  setManifestPrinter(state, printer) {
    state.manifestprinter = printer
  },
  setDefaultDeliverymethod(state, deliverymethod) {
    state.defaultDeliverymethod = deliverymethod
  },
  setPosDeliveryActive(state, status) {
    state.posDeliveryActive = status
  },
  setTabId (state, tabid) {
    state.tabid = tabid
  },
  setFloating (state, floating) {
    state.floating = floating
  },
  setFloatingReceipt (state, floatingReceipt) {
    state.floatingReceipt = floatingReceipt
  },
  setSelfService (state, selfService) {
    state.selfService = selfService
  },
  setColumnTabsMode (state, mode) {
    state.columnTabsMode = mode === 'grid' || mode === 'gridfixed' ? mode : 'row'
  },
  setContract (state, contract) {
    state.contract = contract
  },
  setActiveOrder (state, order) {
    state.activeOrder = order
  },
  setLayouts (state, layouts) {
    state.layouts = layouts
  },
  setPos (state, pos) {
    state.pos = pos
  },
  setView (state, view) {
    state.view = view
  },
  setBaseDomain (state, baseDomain) {
    state.baseDomain = baseDomain
  },
  setLocalDomain (state, localDomain) {
    state.localDomain = localDomain
  },
  setLocale (state, locale) {
    state.locale = locale
    state.systemLocale = locale
  },
  setUserLocale (state, locale) {
    state.locale = locale
  },
  setVersion (state, version) {
    state.version = version
  },
  setUserData (state, userData) {
    state.userData = userData
    if (userData && userData.language) {
      state.locale = userData.language
    } else {
      state.locale = state.systemLocale
    }
    if (userData && userData.advanced) {
      state.showAllFields = true
    } else {
      state.showAllFields = false
    }
    if (userData && userData.advancedcollections !== undefined) {
      state.advancedCollections = parseInt(userData.advancedcollections)
    } else {
      state.advancedCollections = 0
    }
  },
  setJoinChannel (state, joinChannel) {
    state.joinChannel = joinChannel
  },
  setCountries (state, countries) {
    state.countries[state.locale] = countries
  },
  setTranslation (state, translation) {
    state._[state.locale] = translation
  },
  resetBaskets (state) {
    state.basketIds = {}
  },
  setBasketIds (state, data) {
    state.basketIds[data.shopId + '-' + data.index] = data.basketId
  },
  setBasketItems (state, data) {
    if (state.basketItems === null) {
      state.basketItems = {}
    }
    state.basketItems[data.shopId + '-' + data.index] = data.items
  },
  setActiveBasket (state, basketIndex) {
    state.activeBasket = basketIndex
  },
  setPayments (state, data) {
    if (state.payments === null) {
      state.payments = {}
    }
    state.payments[data.shopId + '-' + data.index] = data.payments
  },
  setMobilepayPhoneNumber (state, phoneNumber) {
    state.mobilepayPhoneNumber = phoneNumber
  },
  setMobilepayToken (state, token) {
    state.mobilepayToken = token
  },
  setActiveDelivery (state, data) {
    state.activeDeliveries[data.shopId + '-' + data.index] = data.deliveryId
  },
  setImageIndex (state, index) {
    state.imageIndex = index
  },
  setEditMode (state, mode) {
    state.editMode = mode
  },
  setOpenCategories (state, oc) {
    state.openCategories = oc
  },
  setOpenResourcepools (state, orp) {
    state.openResourcepools = orp
  },
  setUnsavedChanges (state, unsavedChanges) {
    state.unsavedChanges = unsavedChanges
  },
  addUpload (state, upload) {
    state.uploads.push(upload)
  },
  updateUpload (state, { id, ...changes }) {
    const upload = state.uploads.find(u => u.id === id)
    if (upload) {
      Object.assign(upload, changes)
    }
  },
  removeUpload (state, id) {
    state.uploads = state.uploads.filter(u => u.id !== id)
  },
  setIncomingOffersShown (state, value) {
    state.incomingOffersShown = value
  },
  setCampaignCheckDone (state, value) {
    state.campaignCheckDone = value
  },
  setLastSeenMessageTs (state, value) {
    state.lastSeenMessageTs = value
  },
  setSideMenuOpen(state, value) {
    state.sideMenuOpen = value
  },
  setShowAllFields(state, value) {
    state.showAllFields = parseInt(value)
  },
  setAdvancedCollections(state, value) {
    const parsed = parseInt(value)
    state.advancedCollections = parsed
    if (state.userData) {
      state.userData.advancedcollections = parsed
    }
  },
  setHeartbeatTs(state, value) {
    state.heartbeatTs = value
  },
  setSessionExpired (state, sessionExpired) {
    state.sessionExpired = sessionExpired
  },
  setSessionNeedsRecovery (state, sessionNeedsRecovery) {
    state.sessionNeedsRecovery = sessionNeedsRecovery
  }
}

export const getters = {
  allSettings (state) {
    return state.settings
  },
  timezone (state) {
    return state.timezone
  },
  events (state) {
    return state.events
  },
  allCustomers (state) {
    return state.customers
  },
  allBillingcustomers (state) {
    return state.billingcustomers
  },
  linkedStores (state) {
    return state.linkedStores
  },
  vouchers (state) {
    return state.vouchers
  },
  shopId (state) {
    return state.shopId
  },
  hostName (state) {
    return state.hostName
  },
  user (state) {
    return state.user
  },
  apiKey (state) {
    if (state.authToken) {
      return state.authToken
    }
    return state.apiKey
  },
  authToken (state) {
    return state.authToken
  },
  clientid (state) {
    return state.clientid
  },
  clientName (state) {
    return state.clientName
  },
  clientIdNumber (state) {
    return state.clientIdNumber
  },
  manifestprinter (state) {
    return state.manifestprinter
  },
  defaultDeliverymethod (state) {
    return state.defaultDeliverymethod
  },
  posDeliveryActive (state) {
    return state.posDeliveryActive
  },
  posDeliveryAvailable (state) {
    return !!state.defaultDeliverymethod
  },
  tabid (state) {
    return state.tabid
  },
  floating (state) {
    return state.floating
  },
  floatingReceipt (state) {
    return state.floatingReceipt
  },
  selfService (state) {
    return state.selfService
  },
  columnTabsMode (state) {
    return state.columnTabsMode
  },
  contract (state) {
    return state.contract
  },
  activeOrder (state) {
    return state.activeOrder
  },
  layouts (state) {
    return state.layouts
  },
  pos (state) {
    return state.pos
  },
  view (state) {
    return state.view
  },
  baseDomain (state) {
    return state.baseDomain
  },
  uploads (state) {
    return state.uploads
  },
  activeUploads (state) {
    return state.uploads.filter(u => ['queued', 'fetching', 'uploading', 'processing'].includes(u.status))
  },
  pendingAttachments (state) {
    return state.uploads.filter(u => u.status === 'done' && u.owner && !u.applied)
  },
  localDomain (state) {
    return state.localDomain
  },
  locale (state) {
    return state.locale
  },
  version (state) {
    return state.version ? state.version : state.settings && state.shopId && state.locale && state.settings[state.shopId + '-' + state.locale] ? state.settings[state.shopId + '-' + state.locale].defaultversion : null
  },
  userData (state) {
    return state.userData
  },
  joinChannel (state) {
    return state.joinChannel
  },
  basketIds (state) {
    return state.basketIds
  },
  basketItems (state) {
    return state.basketItems
  },
  payments (state) {
    return state.payments
  },
  mobilepayPhoneNumber (state) {
    return state.mobilepayPhoneNumber
  },
  mobilepayToken (state) {
    return state.mobilepayToken
  },
  activeBasket (state) {
    return state.activeBasket
  },
  activeDelivery (state) {
    return state.activeDeliveries
  },
  timeDiff (state) {
    return state.timeDiff
  },
  editMode (state) {
    return state.editMode
  },
  countries (state) {
    return state.countries[state.locale]
  },
  openCategories (state) {
    return state.openCategories
  },
  openResourcepools (state) {
    return state.openResourcepools
  },
  unsavedChanges (state) {
    return state.unsavedChanges
  },
  incomingOffersShown (state) {
    return state.incomingOffersShown
  },
  campaignCheckDone (state) {
    return state.campaignCheckDone
  },
  lastSeenMessageTs (state) {
    return state.lastSeenMessageTs
  },
  sideMenuOpen(state) {
    return state.sideMenuOpen
  },
  showAllFields(state) {
    return state.showAllFields
  },
  advancedCollections(state) {
    return state.advancedCollections
  },
  heartbeatTs(state) {
    return state.heartbeatTs
  },
  sessionNeedsRecovery (state) {
    return state.sessionNeedsRecovery
  },
  dateFormat (state, getters) {
    const momentFormat = getters.pickerDateFormat
    // Convert moment.js format to Luxon format
    return momentFormat
      .replace(/YYYY/g, 'yyyy')
      .replace(/YY/g, 'yy')
      .replace(/MMMM/g, 'LLLL')
      .replace(/MMM/g, 'LLL')
      .replace(/MM/g, 'LL')
      .replace(/M/g, 'L')
      .replace(/DD/g, 'dd')
      .replace(/D/g, 'd')
  },
  timeFormat (state, getters) {
    const momentFormat = getters.pickerTimeFormat
    // Convert moment.js format to Luxon format
    return momentFormat
      .replace(/A/g, 'a')
      .replace(/YYYY/g, 'yyyy')
      .replace(/YY/g, 'yy')
      .replace(/DD/g, 'dd')
      .replace(/D/g, 'd')
  },
  pickerDateFormat (state) {
    if (state.userData && state.userData.dateformat) {
      return state.userData.dateformat
    }
    if (state.settings && state.shopId && state.locale && state.settings[state.shopId + '-' + state.locale] && state.settings[state.shopId + '-' + state.locale].dateformat) {
      return state.settings[state.shopId + '-' + state.locale].dateformat
    }
    if (state._ && state.locale && state._[state.locale] && state._[state.locale]['YYYY/MM/DD']) {
      return state._[state.locale]['YYYY/MM/DD']
    }
    return 'D.M.YYYY'
  },
  pickerTimeFormat (state) {
    if (state.userData && state.userData.timeformat) {
      return state.userData.timeformat
    }
    if (state.settings && state.shopId && state.locale && state.settings[state.shopId + '-' + state.locale] && state.settings[state.shopId + '-' + state.locale].timeformat) {
      return state.settings[state.shopId + '-' + state.locale].timeformat
    }
    if (state._ && state.locale && state._[state.locale] && state._[state.locale]['h.mm A']) {
      return state._[state.locale]['h.mm A']
    }
    return 'H.mm'
  },
  numberFormat (state) {
    if (state.userData && state.userData.numberformat) {
      return state.userData.numberformat
    }
    if (state.settings && state.shopId && state.locale && state.settings[state.shopId + '-' + state.locale] && state.settings[state.shopId + '-' + state.locale].numberformat) {
      return state.settings[state.shopId + '-' + state.locale].numberformat
    }
    return 'fi-FI'
  }
}

export const actions = {
  setTimezone ({ commit }, timezone) {
    commit('setTimezone', timezone)
  },
  setUser({ commit }, user) {
    commit('setUser', user)
  },
  setApiKey({ commit }, apiKey) {
    commit('setApiKey', apiKey)
  },
  setAuthToken({ commit }, authToken) {
    commit('setAuthToken', authToken)
  },
  setClientId({ commit }, clientId) {
    commit('setClientId', clientId)
  },
  setClientName ({ state, commit }, clientName) {
    commit('setClientName', clientName)
  },
  setClientIdNumber ({ state, commit }, clientIdNumber) {
    commit('setClientIdNumber', clientIdNumber)
  },
  setManifestPrinter ({ state, commit }, printer) {
    commit('setManifestPrinter', printer)
  },
  setDefaultDeliverymethod ({ state, commit }, deliverymethod) {
    commit('setDefaultDeliverymethod', deliverymethod)
  },
  setPosDeliveryActive ({ state, commit }, status) {
    commit('setPosDeliveryActive', status)
  },
  setTabId ({ state, commit }, tabid) {
    commit('setTabId', tabid)
  },
  setFloating ({ state, commit }, floating) {
    commit('setFloating', floating)
  },
  setFloatingReceipt ({ state, commit }, floatingReceipt) {
    commit('setFloatingReceipt', floatingReceipt)
  },
  setSelfService ({ state, commit }, selfService) {
    commit('setSelfService', selfService)
  },
  setColumnTabsMode ({ commit }, mode) {
    commit('setColumnTabsMode', mode)
  },
  setContract ({ state, commit }, contract) {
    commit('setContract', contract)
  },
  setActiveOrder ({ state, commit }, order) {
    commit('setActiveOrder', order)
  },
  setLayouts ({ state, commit }, layouts) {
    commit('setLayouts', layouts)
  },
  setPos ({ state, commit }, pos) {
    commit('setPos', pos)
  },
  setView ({ state, commit }, view) {
    commit('setView', view)
  },
  setTimeDiff ({ state, commit }, diff) {
    commit('setTimeDiff', diff)
  },
  setHostName ({ state, commit }, hostName) {
    commit('setHostName', hostName)
  },
  setBaseDomain ({ state, commit }, baseDomain) {
    commit('setBaseDomain', baseDomain)
  },
  setLocalDomain ({ state, commit }, localDomain) {
    commit('setLocalDomain', localDomain)
  },
  setShopId ({ state, commit }, shopId) {
    commit('setShopId', shopId)
  },
  setSettings ({ state, commit }, data) {
    commit('setSettings', data)
  },
  setEvent ({ state, commit }, data) {
    commit('setEvent', data)
  },
  setCustomer ({ state, commit }, data) {
    commit('setCustomer', data)
  },
  setBillingcustomer ({ state, commit }, data) {
    commit('setBillingcustomer', data)
  },
  setLinkedStores ({ state, commit }, data) {
    // await axios.post(state.localDomain + 'linkedstores', data)
    commit('setLinkedStores', data)
  },
  setVouchers ({ state, commit }, data) {
    commit('setVouchers', data)
  },
  setActiveBasket ({ state, commit }, data) {
    commit('setActiveBasket', data)
  },
  setImageIndex ({ state, commit }, index) {
    commit('setImageIndex', index)
  },
  toggleEditMode({ state, commit }) {
    commit('setEditMode', !state.editMode)
  },
  setOpenCategories({ state, commit }, data) {
    commit('setOpenCategories', data)
  },
  setOpenResourcepools({ state, commit }, data) {
    commit('setOpenResourcepools', data)
  },
  setUnsavedChanges({ state, commit }, data) {
    commit('setUnsavedChanges', data)
  },
  setIncomingOffersShown({ state, commit }, data) {
    commit('setIncomingOffersShown', data)
  },
  setCampaignCheckDone({ state, commit }, data) {
    commit('setCampaignCheckDone', data)
  },
  setLastSeenMessageTs({ state, commit }, data) {
    commit('setLastSeenMessageTs', data)
  },
  setSideMenuOpen({ state, commit }, data) {
    commit('setSideMenuOpen', data)
  },
  setShowAllFields({ state, commit }, data) {
    commit('setShowAllFields', data)
  },
  setAdvancedCollections({ state, commit }, data) {
    commit('setAdvancedCollections', data)
  },
  setHeartbeatTs({ state, commit }, data) {
    commit('setHeartbeatTs', Math.floor(Date.now() / 1000))
  },
  setSessionExpired ({ commit }, sessionExpired) {
    commit('setSessionExpired', sessionExpired)
  },
  setSessionNeedsRecovery ({ commit }, sessionNeedsRecovery) {
    commit('setSessionNeedsRecovery', sessionNeedsRecovery)
  },
  async reloadSettings ({ state, getters, dispatch, commit }) {
    const locale = state.locale
    const localeStr = locale ? '/' + locale : ''
    const apiKey = effectiveApiKey(state)
    const query = 'version=' + locale + '&pricetiers=true&files=true&alltags=true'
    let authRejected = false
    let settings = null

    if (apiKey && String(apiKey).toLowerCase() !== 'null' && String(apiKey).toLowerCase() !== 'undefined') {
      try {
        const response = await axios.get(state.baseDomain + state.shopId + localeStr + '/settings.json?' + query + '&deliverymethods=true', {
          headers: { Authorization: 'Bearer ' + apiKey }
        })
        settings = response.data
      } catch (error) {
        authRejected = isAuthStatusError(error)
        if (authRejected && isBetterAuthApiKey(apiKey)) {
          const confirmed = await confirmBetterAuthSession({ state, getters, dispatch })
          if (confirmed.status !== 'expired') {
            authRejected = false
          }
        }
      }
    }

    if (!settings) {
      try {
        const response = await axios.get(state.baseDomain + state.shopId + localeStr + '/settings/public.json?' + query)
        settings = response.data
      } catch {
        settings = null
      }
    }

    if (authRejected) {
      commit('setUser', null)
      commit('setAuthToken', null)
      commit('setSessionExpired', true)
    } else {
      commit('setSessionExpired', false)
    }

    if (settings) {
      dispatch('setSettings', {
        shopId: state.shopId,
        locale,
        settings
      })
    } else {
      console.log(state.shopId, 'Settings not found')
      // Don't clobber already-known-good settings with an empty object just because this
      // (re)load attempt failed - that would cause a flash of "shop not found" content
      // (e.g. right after logout, while the session cookies are still settling server-side).
      const existingSettings = state.settings[state.shopId + '-' + locale]
      if (!existingSettings || !existingSettings.id) {
        dispatch('setSettings', {
          shopId: state.shopId,
          locale,
          settings: {}
        })
      }
    }

    return axios.all([
      axios.get(state.baseDomain + state.shopId + localeStr + '/linkedstores/public.json?version=' + state.locale)
      .catch(() => {
        console.log(state.shopId, 'Linked store not found')
        dispatch('setLinkedStores', {
          linkedStores: []
        })
      }),
      axios.get(state.baseDomain + state.shopId + localeStr + '/vouchers.json?iSortingCols=1&iSortDir_0=2')
      .catch(() => {
        console.log(state.shopId, 'Vouchers not found')
        dispatch('setVouchers', {
          vouchers: []
        })
      })
    ])
    .then(axios.spread(async function (linkedStores, vouchers) {
      dispatch('setLinkedStores', {
        linkedStores: linkedStores.data
      })
      dispatch('setVouchers', {
        vouchers: vouchers.data
      })
      if (linkedStores && linkedStores.data && linkedStores.data.length) {
        for (var i = 0; i < linkedStores.data.length; i++) {
          let linkedStoreShopId = linkedStores.data[i].shopid
          await axios.get(state.baseDomain + linkedStoreShopId + localeStr + '/settings/public.json?version=' + state.locale + '&pricetiers=true')
          .then(function (linkedSettings) {
            dispatch('setSettings', {
              shopId: linkedStoreShopId,
              locale: state.locale,
              settings: linkedSettings.data
            })
          })
          .catch(() => {
            // A single stale/removed linked shop (e.g. 404 with no CORS headers, which
            // surfaces as an opaque network error) must not abort this loop - letting it
            // propagate uncaught would reject the whole chain below and clobber the
            // current shop's own already-fetched settings with an empty object.
            console.log(linkedStoreShopId, 'Linked store settings not found')
          })
        }
      }
    }))
    .catch(() => {
      console.log(state.shopId, 'Error loading settings.')
      dispatch('setSettings', {
        shopId: state.shopId,
        locale: locale,
        settings: {}
      })
    })
  },
  async syncBackendAuth ({ state, commit }, { shopId, clientid, authToken, staffReason, staffReasonDetail }) {
    try {
      const data = await $fetch('/api/auth/backend-sync', {
        method: 'POST',
        body: {
          shopId: shopId ?? state.shopId,
          clientid: clientid ?? state.clientid,
          staffReason,
          staffReasonDetail
        }
      })
      if (data?.user == null || data?.user === '') {
        throw new Error('You do not have access to this shop')
      }
      commit('setUser', data.user)
      if (authToken) {
        commit('setAuthToken', authToken)
      }
    } catch (error) {
      // Nitro wraps thrown createError({ data }) as { statusCode, statusMessage, data } in
      // the HTTP response body, so the staff-access payload we set server-side ends up
      // nested one level deeper (error.data.data) once it crosses the fetch boundary.
      const body = error?.data || error?.response?._data
      const payload = body?.data || body
      if (payload?.code === 'STAFF_REASON_REQUIRED' || payload?.errorCode === 'STAFF_REASON_REQUIRED') {
        const staffError = new Error('STAFF_REASON_REQUIRED')
        staffError.reasonPresets = payload.reasonPresets || []
        throw staffError
      }
      if (payload?.code === 'POS_SESSION_EXPIRED') {
        throw new Error('POS_SESSION_EXPIRED')
      }
      throw new Error('You do not have access to this shop')
    }
  },
  async logout ({ state, getters, commit, dispatch }) {
    suppressSessionRecovery(true)
    clearStaffReasonFlow()
    try {
      try {
        // The key lets the backend end this user's own staff access log (it no longer trusts ?user alone)
        await axios.post(state.localDomain + 'logout', { shopId: state.shopId, clientid: state.clientid, user: state.user, apiKey: getters.apiKey })
      } catch (e) {}
      try {
        // Signing out through the SDK client (rather than a raw $fetch) ensures
        // Better Auth's internal $sessionSignal fires, so every useSession() ref
        // sharing this shop's client (see auth-client.ts) immediately reflects the
        // signed-out state instead of continuing to serve a stale cached session.
        await createShopAuthClient(state.shopId).signOut()
      } catch (e) {}
      commit('setUser', null)
      commit('setApiKey', null)
      commit('setAuthToken', null)
      commit('setSessionExpired', false)
      commit('setSessionNeedsRecovery', false)
      commit('setUserData', null)
      commit('setJoinChannel', null)
      commit('setClientName', null)
      commit('setClientIdNumber', null)
      commit('setManifestPrinter', null)
      commit('setFloating', null)
      commit('setFloatingReceipt', null)
      commit('setSelfService', null)
      commit('setColumnTabsMode', null)
      commit('setContract', null)
      commit('setPos', null)
      commit('resetBaskets')
      commit('setLinkedStores', {
        linkedStores: []
      })
      dispatch('reloadSettings')
    } finally {
      suppressSessionRecovery(false)
    }
  },
  async clientid({ state, commit }, input) {
    try {
      if (state.localDomain && state.shopId) {
        const params = { shopId: state.shopId }
        const existingClientId = input?.clientid || state.clientid
        if (existingClientId) {
          params.clientid = existingClientId
        }
        const { data } = await axios.post(state.localDomain + 'clientid', params, {
          withCredentials: true
        })
        if (data.clientid) {
          commit('setClientId', data.clientid)
        }
      }
    } catch(e) {
      console.log(e)
    }
  },
  async userdata({ state, commit }, reload) {
    if (!reload && state.userData) {
      return
    }
    try {
      const { data } = await axios.get(state.baseDomain + state.shopId + '/users/' + state.user + '.json?apikey=' + effectiveApiKey(state))
      commit('setUserData', data)
    } catch(e) {}
  },
  async joinchannel({ commit }, data) {
    commit('setJoinChannel', data)
  },
  async setLocale ({ state, commit }, locale) {
    if (state._[locale]) {
      return commit('setLocale', locale)
    }
    try {
      let res = await axios.get(`${state.baseDomain}include/locale/pos-${locale}.json`)
      commit('setLocale', locale)
      commit('setTranslation', res.data)
      res = await axios.get(`${state.baseDomain}include/locale/countries-${locale}.json`)
      commit('setCountries', res.data)
    } catch(e) {}
  },
  async setUserLocale ({ state, commit }, locale) {
    if (state._[locale]) {
      return commit('setUserLocale', locale)
    }
    try {
      let res = await axios.get(`${state.baseDomain}include/locale/pos-${locale}.json`)
      commit('setUserLocale', locale)
      commit('setTranslation', res.data)
      res = await axios.get(`${state.baseDomain}include/locale/countries-${locale}.json`)
      commit('setCountries', res.data)
    } catch(e) {}
  },
  setVersion ({ commit }, version) {
    commit('setVersion', version)
  },
  resetBaskets ({ commit }) {
    commit('resetBaskets')
  },
  async setBasketIds ({ state, commit }, data) {
    let localeStr = ''
    if (state.locale) {
      localeStr = '/' + state.locale
    }
    let extraStr = ''
    if (data.shopId !== state.shopId) {
      extraStr += '&shop=' + state.shopId
    }
    if (state.activeOrder) {
      extraStr += '&orderidentifier=' + state.activeOrder.identifier
    }
    let res = { data: [] }
    try {
      res = await axios.get(`${state.baseDomain}${data.shopId}${localeStr}/baskets/${data.basketId}/items.json?client=${state.clientid}${extraStr}`)
    } catch(e) {}
    commit('setBasketIds', data)
    commit('setBasketItems', { shopId: data.shopId, index: data.index, items: res.data })
  },
  setActiveDelivery ({ commit }, data) {
    commit('setActiveDelivery', { shopId: data.shopId, index: data.index, deliveryId: data.deliveryId })
  },
  async reloadBasket ({ state, commit }, basketShopIdWithIndex) {
    let localeStr = ''
    if (state.locale) {
      localeStr = '/' + state.locale
    }
    for (var shopIdWithIndex in state.basketIds) {
      let siArr = shopIdWithIndex.split('-')
      let shopId = siArr[0]
      let index = siArr[1]
      if (Object.prototype.hasOwnProperty.call(state.basketIds, shopIdWithIndex) && (!basketShopIdWithIndex || shopIdWithIndex === basketShopIdWithIndex)) {
        let extraStr = ''
        if (shopId !== state.shopId) {
          extraStr += '&shop=' + state.shopId
        }
        if (state.activeOrder) {
          extraStr += '&orderidentifier=' + state.activeOrder.identifier
        }
        try {
          let res = await axios.get(`${state.baseDomain}${shopId}${localeStr}/baskets/${state.basketIds[shopIdWithIndex]}/items.json?client=${state.clientid}${extraStr}`)
          commit('setBasketItems', { shopId: shopId, index: index, items: res.data })
          EventBus.$emit('setBasketItems')
        } catch(e) {}
      }
    }
  },
  async setBasketItems({ state, commit }, data) {
    commit('setBasketItems', data)
  },
  async reloadPayments ({ state, commit }, basketShopIdWithIndex) {
    let response
    let siArr = basketShopIdWithIndex.split('-')
    let shopId = siArr[0]
    let index = siArr[1]
    const request = (paymentListRequests[basketShopIdWithIndex] || 0) + 1
    paymentListRequests[basketShopIdWithIndex] = request
    try {
      let extraStr = ''
      if (state.activeOrder) {
        extraStr += '&orderidentifier=' + state.activeOrder.identifier
      }
      response = await axios.get(state.baseDomain + shopId + '/baskets/' + state.basketIds[basketShopIdWithIndex] + '/pospayments.json?apikey=' + effectiveApiKey(state) + '&client=' + state.clientid + extraStr)
    } catch(e) {
      console.log('error reloading payments', e)
      return
    }
    if (paymentListRequests[basketShopIdWithIndex] !== request) {
      return
    }
    if (response && response.data) {
      commit('setPayments', { shopId: shopId, index: index, payments: response.data })
    } else {
      commit('setPayments', {})
    }
  },
  /**
   * Payments were removed from the basket: drop the responses of payment list requests that started before that
   */
  invalidatePayments (context, basketShopIdWithIndex) {
    paymentListRequests[basketShopIdWithIndex] = (paymentListRequests[basketShopIdWithIndex] || 0) + 1
  },
  setPayments ({ commit }, data) {
    commit('setPayments', data)
    EventBus.$emit('setPayments')
  },
  setMobilepayPhoneNumber ({ commit }, phoneNumber) {
    commit('setMobilepayPhoneNumber', phoneNumber)
  },
  setMobilepayToken ({ commit }, token) {
    commit('setMobilepayToken', token)
  },
  async deleteAllItemsFromBasket ({ state, commit }, data) {
    let identifier = data.shopId + '-' + state.activeBasket
    commit('setBasketItems', { shopId: data.shopId, index: state.activeBasket, items: [] })
    let extraStr = ''
    if (data.shopId !== state.shopId) {
      extraStr += '&shop=' + state.shopId
    }
    if (state.activeOrder) {
      extraStr += '&orderidentifier=' + state.activeOrder.identifier
    }
    try {
      await axios.delete(`${state.baseDomain}${data.shopId}/baskets/${state.basketIds[identifier]}.json?client=${state.clientid}&apikey=${effectiveApiKey(state)}${extraStr}`)
    } catch(e) {}
    EventBus.$emit('basketEmptied', {
      shopId: data.shopId,
      index: state.activeBasket
    })
    EventBus.$emit('setBasketItems')
  },
  async storeShowAllFields ({ state, commit }, data) {
    if(!state.userData) {
      return
    }
    commit('setShowAllFields', data)
    const clonedUserData = structuredClone(toRaw(state.userData))
    clonedUserData.advanced = parseInt(data)
    try {
      await axios.put(`${state.baseDomain}${state.shopId}/users/${state.userData.id}.json?apikey=${effectiveApiKey(state)}`, clonedUserData)
    } catch(e) {
      if(e.response) {
        console.log(e.response)
      } else {
        console.log(e)
      }
    }
  },
  async storeAdvancedCollections ({ state, commit }, data) {
    if(!state.userData) {
      return
    }
    commit('setAdvancedCollections', data)
    const clonedUserData = structuredClone(toRaw(state.userData))
    clonedUserData.advancedcollections = parseInt(data)
    try {
      await axios.put(`${state.baseDomain}${state.shopId}/users/${state.userData.id}.json?apikey=${effectiveApiKey(state)}`, clonedUserData)
    } catch(e) {
      if(e.response) {
        console.log(e.response)
      } else {
        console.log(e)
      }
    }
  },
  async deleteItemFromBasket ({ state, commit }, data) {
    let identifier = data.shopId + '-' + state.activeBasket
    let tmpItems = state.basketItems[identifier]
    for (var i = 0; i < tmpItems.length; i++) {
      if (tmpItems[i].id == data.id) {
        tmpItems.splice(i, 1)
        break
      }
    }
    commit('setBasketItems', { shopId: data.shopId, index: state.activeBasket, items: tmpItems })
    let extraStr = ''
    if (data.shopId !== state.shopId) {
      extraStr += '&shop=' + state.shopId
    }
    if (state.activeOrder) {
      extraStr += '&orderidentifier=' + state.activeOrder.identifier
    }
    try {
      await axios.delete(`${state.baseDomain}${data.shopId}/baskets/${state.basketIds[identifier]}/items/${data.id}.json?client=${state.clientid}&apikey=${effectiveApiKey(state)}${extraStr}`)
    } catch(e) {}
    EventBus.$emit('removedFromBasket', {
      item: data.item,
      shopId: data.shopId,
      index: state.activeBasket
    })
    EventBus.$emit('setBasketItems')
  },
  async unlinkBasketItems ({ state, commit }, data) {
    let identifier = data.shopId + '-' + state.activeBasket
    let tmpItems = state.basketItems[identifier]
    commit('setBasketItems', { shopId: data.shopId, index: state.activeBasket, items: [] })
    let extraStr = ''
    if (data.shopId !== state.shopId) {
      extraStr += '&shop=' + state.shopId
    }
    if (state.activeOrder) {
      extraStr += '&orderidentifier=' + state.activeOrder.identifier
    }
    let urlArray = []
    for (var i = 0; i < tmpItems.length; i++) {
      let item = tmpItems[i]
      if (item.order_id) {
        urlArray.push(axios.patch(`${state.baseDomain}${data.shopId}/baskets/${state.basketIds[identifier]}/items/${item.id}.json?id=${item.id}&client=${state.clientid}&apikey=${effectiveApiKey(state)}${extraStr}`))
      } else {
        try {
          await axios.delete(`${state.baseDomain}${data.shopId}/baskets/${state.basketIds[identifier]}/items/${item.id}.json?client=${state.clientid}${extraStr}`)
        } catch(e) {}
      }
    }
    axios.all(urlArray)
    .then(axios.spread(function () {
      for (var i = 0; i < arguments.length; i++) {
        let item = tmpItems[i]
        EventBus.$emit('removedFromBasket', {
          item: item,
          shopId: data.shopId,
          index: state.activeBasket
        })
      }
      EventBus.$emit('setBasketItems')
    }))
  },
  async modifyBasketItem ({ state, commit }, data) {
    let identifier = data.shopId + '-' + state.activeBasket
    return new Promise((resolve, reject) => {
      let dataArr = []
      dataArr.push('id=' + data.id)
      dataArr.push('count=' + data.count)
      dataArr.push('price=' + String(data.price).replace(',', '.'))
      dataArr.push('client=' + state.clientid)
      if (data.masterShopId) {
        dataArr.push('shop=' + data.masterShopId)
      }
      if (data.product_id) {
        dataArr.push('product_id=' + data.product_id)
      }
      if (data.split) {
        dataArr.push('split=' + data.split)
      }
      if (data.hasOwnProperty('vat')) {
        dataArr.push('vat=' + data.vat)
      }
      if (data.hasOwnProperty('productName')) {
        dataArr.push('product_name=' + data.productName)
      }
      if (data.hasOwnProperty('special') && data.special !== null) {
        dataArr.push('special=' + data.special)
      }
      if (data.hasOwnProperty('netvisorAccount')) {
        dataArr.push('netvisoraccount=' + data.netvisorAccount)
      }
      if (data.hasOwnProperty('netvisorVatcode')) {
        dataArr.push('netvisorvatcode=' + data.netvisorVatcode)
      }
      if (data.hasOwnProperty('loyalty')) {
        dataArr.push('loyalty=' + data.loyalty)
      }
      if (data.hasOwnProperty('deliveryId')) {
        dataArr.push('delivery_id=' + data.deliveryId)
      }
      if (state.activeOrder) {
        dataArr.push('orderidentifier=' + state.activeOrder.identifier)
      }
      // Convert dataArr strings into object for axios
      const putData = dataArr.reduce((obj, item) => {
        const [key, value] = item.split('=')
        obj[key] = value
        return obj
      }, {})
      let putQueryStr = `client=${state.clientid}&apikey=${effectiveApiKey(state)}`
      if (data.shopId !== state.shopId) {
        putQueryStr += '&shop=' + state.shopId
      }
      if (state.activeOrder) {
        putQueryStr += '&orderidentifier=' + state.activeOrder.identifier
      }
      axios.put(`${state.baseDomain}${data.shopId}/${data.locale}/baskets/${state.basketIds[identifier]}/items/${data.id}.json?${putQueryStr}`, putData)
      .then(function (res) {
        resolve()
      })
      .catch(function (err) {
        let shopStr = ''
        if (data.shopId !== state.shopId) {
          shopStr = '&shop=' + state.shopId
        }
        axios.get(`${state.baseDomain}${data.shopId}/${data.locale}/baskets/${state.basketIds[identifier]}/items.json?client=${state.clientid}${shopStr}`)
        .then(function (items) {
          commit('setBasketItems', { shopId: data.shopId, index: state.activeBasket, items: items.data })
          EventBus.$emit('setBasketItems')
          if (err.response) {
            reject(err.response.data)
          } else {
            reject()
          }
        })
      })
    })
  },
  async addToBasket ({ state, commit }, data) {
    // Create a fake item and push it tentatively to the basketItems array. The basket will be updated with real data once the operation completes.
    let identifier = data.shopId + '-' + state.activeBasket
    let tmpItems = state.basketItems[identifier]
    let i = Object.assign({}, data)
    if (data.product) {
      i.product_id = data.product.id
      i.product_name = data.product.marketingname && parseInt(data.product.usemarketingname) ? data.product.marketingname : data.product.name
      i.count = data.count
      i.vat = data.product.vat
      i.currency = data.product.currency
      i.price = parseFloat(String(data.product.price).replace(',','.'))
      tmpItems.push(i)
    }
    commit('setBasketItems', { shopId: data.shopId, index: state.activeBasket, items: tmpItems })
    return Queue.enqueue(() => new Promise(function (resolve, reject) {
      let dataArr = []
      if (state.posDeliveryActive) {
        dataArr.push('delivery_id=pos')
      } else {
        dataArr.push('delivery_id=auto')
      }
      if (data.shopId !== state.shopId) {
        dataArr.push('shop=' + state.shopId)
      }
      dataArr.push('client=' + state.clientid)
      dataArr.push('version=' + (data.version ? data.version : data.locale))
      if (data.product) {
        dataArr.push('product_id=' + data.product.id)
      } else if (data.productName) {
        dataArr.push('product_name=' + data.productName)
      }
      if (data.hasOwnProperty('price')) {
        dataArr.push('price=' + String(data.price).replace(',','.'))
      }
      if (data.hasOwnProperty('vat')) {
        dataArr.push('vat=' + data.vat)
      }
      if (data.code) {
        dataArr.push('code=' + data.code)
      }
      if (data.item) {
        dataArr.push('item_id=' + data.item.id)
      }
      if (data.valuecard) {
        dataArr.push('valuecard=' + data.valuecard)
      }
      if (data.voucher) {
        dataArr.push('voucher=' + data.voucher)
      }
      if (data.amount) {
        dataArr.push('amount=' + data.amount)
      }
      if (data.showId) {
        dataArr.push('show_id=' + data.showId)
      }
      if (data.ticket) {
        dataArr.push('ticket=' + data.ticket)
        if (data.pricing) {
          dataArr.push('pricing=' + data.pricing)
        }
      }
      if (data.selectedSeats) {
        dataArr.push('seat_ids=' + data.selectedSeats)
      }
      if (data.seatTicket) {
        dataArr.push('seat_ticket=' + data.seatTicket)
      }
      if (data.count) {
        dataArr.push('count=' + data.count)
      }
      if (data.resourceCount) {
        dataArr.push('resource_count=' + data.resourceCount)
      }
      if (data.reservationCount) {
        dataArr.push('reservation_count=' + data.reservationCount)
      }
      if (data.reservationDateStart) {
        dataArr.push('reservation_datestart=' + data.reservationDateStart)
      }
      if (data.reservationDateEnd) {
        dataArr.push('reservation_dateend=' + data.reservationDateEnd)
      }
      if (data.reservationTimeStart) {
        dataArr.push('reservation_timestart=' + data.reservationTimeStart)
      }
      if (data.reservationTimeEnd) {
        dataArr.push('reservation_timeend=' + data.reservationTimeEnd)
      }
      if (data.maintenanceMode) {
        dataArr.push('maintenance_window=' + data.maintenanceMode)
      }
      if (data.origin) {
        dataArr.push('origin=' + data.origin)
      }
      if (data.destination) {
        dataArr.push('destination=' + data.destination)
      }
      if (data.roundTrip) {
        dataArr.push('roundtrip=1')
        dataArr.push('return_date=' + data.returnDate)
        dataArr.push('return_time=' + data.returnTime)
        dataArr.push('return_endtime=' + data.returnEndTime)
      }
      if (data.linkType) {
        dataArr.push('linktype=' + data.linkType)
      }
      if (data.hasOwnProperty('special')) {
        dataArr.push('special=' + data.special)
      }
      if (data.hasOwnProperty('netvisorAccount')) {
        dataArr.push('netvisoraccount=' + data.netvisorAccount)
      }
      if (data.hasOwnProperty('netvisorVatcode')) {
        dataArr.push('netvisorvatcode=' + data.netvisorVatcode)
      }
      if (state.activeOrder) {
        dataArr.push('orderidentifier=' + state.activeOrder.identifier)
      }
      if (data.voucherPrice) {
        dataArr.push('price=' + String(data.voucherPrice).replace(',','.'))
      }
      if (data.resourcepools) {
        dataArr.push('resourcepools=' + data.resourcepools)
      }
      for (var key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          if (key.match(/^pricetier/) && parseInt(data[key])) {
            dataArr.push(key + '=' + data[key])
          }
          if (key.match(/^pool/) && parseInt(data[key])) {
            dataArr.push(key + '=' + data[key])
          }
        }
      }
      // Convert dataArr strings into object for axios
      const postData = dataArr.reduce((obj, item) => {
        const [key, value] = item.split('=')
        obj[key] = value
        return obj
      }, {})
      let itemId = 'new'
      if (data.code && data.item_id) {
        itemId = data.item_id
      }
      let postQueryStr = `client=${state.clientid}&apikey=${effectiveApiKey(state)}`
      if (data.shopId !== state.shopId) {
        postQueryStr += '&shop=' + state.shopId
      }
      if (state.activeOrder) {
        postQueryStr += '&orderidentifier=' + state.activeOrder.identifier
      }
      axios.post(`${state.baseDomain}${data.shopId}/${data.locale}/baskets/${state.basketIds[identifier]}/items/${itemId}.json?${postQueryStr}`, postData)
      .then(function (res) {
        if (data.product) {
          EventBus.$emit('addedToBasket', {
            product: data.product,
            shopId: data.shopId,
            index: state.activeBasket,
            count: data.count
          })
        } else if (data.code) {
          EventBus.$emit('addedToBasket', {
            code: data.code,
            id: data.id,
            shopId: data.shopId,
            index: state.activeBasket,
            count: 1
          })
        } else if (data.valuecard) {
          EventBus.$emit('addedToBasket', {
            valuecard: data.valuecard,
            id: data.id,
            shopId: data.shopId,
            index: state.activeBasket,
            count: data.count
          })
        } else if (data.voucher) {
          EventBus.$emit('addedToBasket', {
            voucher: data.voucher,
            id: data.id,
            shopId: data.shopId,
            index: state.activeBasket,
            count: data.count
          })
        }
        EventBus.$emit('setBasketItems')
        resolve()
      })
      .catch(function (err) {
        let extraStr = ''
        if (data.shopId !== state.shopId) {
          extraStr += '&shop=' + state.shopId
        }
        if (state.activeOrder) {
          extraStr += '&orderidentifier=' + state.activeOrder.identifier
        }
        axios.get(`${state.baseDomain}${data.shopId}/${data.locale}/baskets/${state.basketIds[identifier]}/items.json?client=${state.clientid}${extraStr}`)
        .then(function (items) {
          commit('setBasketItems', { shopId: data.shopId, index: state.activeBasket, items: items.data })
          EventBus.$emit('setBasketItems')
          if (err.response) {
            reject(err.response.data)
          } else {
            reject()
          }
        })
        .catch(function(e) {
          // In case the get also fails, let's return the previous request's error
          if (err.response) {
            reject(err.response.data)
          } else {
            reject()
          }
        })
      })
    }))
  }
}

export const setEventBus = (eventBus) => {
  EventBus = eventBus
}